'use client';

import { useId } from 'react';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { doorSvg } from '../drawing/door';
import { SvgArt } from '../drawing/SvgArt';
import { safeHex } from '../drawing/svg';
import {
  childLevel,
  headingLevel,
  items,
  SkImage,
  SkLink,
  text,
  type HeadingLevel,
  type Localized,
  type SkImageRef,
} from '../shared/primitives';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';
import { dimension, doorStyleOr, homeSurfaceClass, HomeSurfaceStyles, Italicised, type HomeSurface } from './homeShared';

/**
 * Text beside a picture, for a secondary department (bedrooms) or an audience
 * (trade). The mockup's BEDROOMS and TRADE sections are this one block.
 *
 * <p>The art side is one of:
 * <ul>
 *   <li>`doors`: a row of one to four drawn doors on a warm panel. Decorative.</li>
 *   <li>`cards`: up to four small cards (eyebrow, title, text). These are
 *       CONTENT, read in order after the text, with titles one level below the
 *       section heading. Figures on them (discounts, terms) are the owner's to
 *       write; the block ships none.</li>
 *   <li>`image`: an uploaded picture with its own alt text.</li>
 * </ul>
 *
 * <p>The text always comes first in the document, so it is read first; the
 * `artSide` switch only moves the art visually, from 900 px up. Below that the
 * two stack, text first.
 */
export type SplitArtKind = 'doors' | 'cards' | 'image';

export interface SpatialKitchenSplitDoor {
  style?: string;
  colour?: string;
  /** Drawing width and height in mm. */
  w?: number;
  h?: number;
}

export interface SpatialKitchenSplitCard {
  eyebrow?: Localized;
  title?: Localized;
  body?: Localized;
}

export interface SpatialKitchenSplitCta {
  label?: Localized;
  href?: string;
}

export interface SpatialKitchenSplitProps {
  eyebrow?: Localized;
  title?: Localized;
  /** One phrase inside `title` to set in italic. */
  emphasis?: Localized;
  lede?: Localized;
  bullets?: Localized[];
  ctas?: SpatialKitchenSplitCta[];
  artKind?: SplitArtKind;
  artSide?: 'left' | 'right';
  doors?: SpatialKitchenSplitDoor[];
  cards?: SpatialKitchenSplitCard[];
  image?: SkImageRef;
  surface?: HomeSurface;
  headingLevel?: HeadingLevel;
}

const CSS = `
.sk-split__grid{display:grid;gap:2.5rem;align-items:center}
@media (min-width:900px){
  .sk-split__grid{grid-template-columns:repeat(2,minmax(0,1fr));gap:56px}
  .sk-split--art-left .sk-split__art{order:-1}
}
.sk-split__text{display:grid;min-width:0}
.sk-split__text .sk-eyebrow{margin-bottom:12px}
.sk-split__lede{margin-top:16px;max-width:58ch}
.sk-split__bullets{display:grid;gap:6px;margin:22px 0 0;padding-left:18px;color:var(--sk-muted)}
.sk-split__ctas{display:flex;flex-wrap:wrap;gap:12px;margin:28px 0 0}
@media (max-width:479px){.sk-split__ctas .sk-btn{white-space:normal;text-align:center}}
.sk-split__art{min-width:0}
.sk-split__doors{display:flex;justify-content:center;align-items:flex-end;gap:10px;min-height:clamp(260px,40vw,420px);padding:clamp(20px,4vw,40px);border-radius:22px;background:linear-gradient(180deg,#efe7da,#e3d8c6);overflow:hidden}
.sk-split__doors .sk-art{display:block;flex:0 1 auto;min-width:0;height:clamp(180px,30vw,340px)}
.sk-split__doors svg{display:block;height:100%;width:auto;max-width:100%}
.sk-split__cards{display:grid;gap:14px;margin:0;padding:0;list-style:none}
@media (min-width:480px){.sk-split__cards{grid-template-columns:repeat(2,minmax(0,1fr))}}
.sk-split__card{padding:22px;min-width:0}
.sk-split__card .sk-eyebrow{margin-bottom:10px}
.sk-split__card-title{font-size:1.35rem;margin-bottom:8px}
.sk-split__card p{margin:0;font-size:.95rem;color:var(--sk-muted)}
.sk-split__image{border-radius:22px;aspect-ratio:4/3}
`;

function DoorRow({ doors }: { doors: SpatialKitchenSplitDoor[] }) {
  const artId = useId();
  return (
    <div className="sk-split__doors" aria-hidden="true">
      {doors.map((door, index) => (
        <SvgArt
          key={index}
          svg={doorSvg(doorStyleOr(door.style), safeHex(door.colour), {
            w: dimension(door.w, 220),
            h: dimension(door.h, 900),
            // `_` survives idSource's sanitising and never occurs in a React id, so doors cannot collide.
            idPrefix: `${artId}_${index}`,
          })}
        />
      ))}
    </div>
  );
}

export function SpatialKitchenSplit(props: SpatialKitchenSplitProps) {
  const level = headingLevel(props.headingLevel, 'h2');
  const Title = level;
  const CardTitle = childLevel(level);
  const titleId = useId();
  const title = text(props.title, 'Section title');
  const lede = text(props.lede);
  const bullets = (Array.isArray(props.bullets) ? props.bullets : []).map((bullet) => text(bullet)).filter(Boolean);
  const ctas = items<SpatialKitchenSplitCta>(props.ctas)
    .filter((cta) => text(cta.label) && cta.href)
    .slice(0, 2);
  const doors = items<SpatialKitchenSplitDoor>(props.doors).slice(0, 4);
  const cards = items<SpatialKitchenSplitCard>(props.cards)
    .filter((card) => text(card.title) || text(card.body))
    .slice(0, 4);
  const kind: SplitArtKind = props.artKind === 'cards' || props.artKind === 'image' ? props.artKind : 'doors';
  const artLeft = props.artSide === 'left';

  let art = null;
  if (kind === 'image' && props.image?.src) {
    art = <SkImage image={props.image} className="sk-split__image" sizes="(min-width: 900px) 50vw, 100vw" />;
  } else if (kind === 'cards' && cards.length > 0) {
    art = (
      <ul className="sk-split__cards">
        {cards.map((card, index) => (
          <li key={`${text(card.title)}-${index}`} className="sk-card sk-split__card">
            {text(card.eyebrow) && <p className="sk-eyebrow">{text(card.eyebrow)}</p>}
            {text(card.title) && <CardTitle className="sk-heading sk-split__card-title">{text(card.title)}</CardTitle>}
            {text(card.body) && <p>{text(card.body)}</p>}
          </li>
        ))}
      </ul>
    );
  } else if (kind === 'doors') {
    art = <DoorRow doors={doors.length > 0 ? doors : [{}]} />;
  }

  return (
    <BlockRoot
      as="section"
      className={`sk sk-split sk-section ${homeSurfaceClass(props.surface, 'white')}${artLeft ? ' sk-split--art-left' : ''}`}
      aria-labelledby={titleId}
    >
      <SkBaseStyles />
      <HomeSurfaceStyles />
      <SkStyles id="split" css={CSS} />
      <div className="sk-container sk-split__grid">
        <div className="sk-split__text">
          {text(props.eyebrow) && <p className="sk-eyebrow">{text(props.eyebrow)}</p>}
          <Title id={titleId} className="sk-heading sk-h2">
            <Italicised title={title} emphasis={text(props.emphasis)} />
          </Title>
          {lede && <p className="sk-lead sk-split__lede">{lede}</p>}
          {bullets.length > 0 && (
            <ul className="sk-split__bullets">
              {bullets.map((bullet, index) => (
                <li key={`${bullet}-${index}`}>{bullet}</li>
              ))}
            </ul>
          )}
          {ctas.length > 0 && (
            <p className="sk-split__ctas">
              {ctas.map((cta, index) => (
                <SkLink
                  key={`${cta.href}-${index}`}
                  href={cta.href}
                  className={`sk-btn ${index === 0 ? 'sk-btn--primary' : 'sk-btn--secondary'}`}
                >
                  {text(cta.label)}
                </SkLink>
              ))}
            </p>
          )}
        </div>
        {art && <div className="sk-split__art">{art}</div>}
      </div>
    </BlockRoot>
  );
}
